import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Grid, TextField, Button, Checkbox, FormControlLabel,
  Stack, Alert, MenuItem, Divider, Radio, RadioGroup, FormControl,
  FormLabel, Chip, Tooltip, IconButton, InputAdornment
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ScaleIcon from '@mui/icons-material/Scale';

import {
  getPdvById, createPdv, updatePdv, getEstabelecimentos, solicitarCargaBalancaPdv,
  getAllPdvs, getLocaisEstoque,
} from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';
import useModulosLicenca from '../hooks/useModulosLicenca';
import CenteredAlert from './common/CenteredAlert';

const zen = {
  blue: '#1976d2',
  blueDark: '#155fa0',
  sectionTitleSx: {
    background: '#1976d2',
    color: '#fff',
    fontWeight: 800,
    letterSpacing: 0.3,
    px: 2,
    py: 1,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
  },
  sectionCardSx: {
    borderRadius: 2,
    border: '1px solid rgba(0,0,0,0.08)',
    boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
    backgroundColor: 'background.paper'
  },
};

function Section({ title, children, sx }) {
  return (
    <Box sx={{ ...zen.sectionCardSx, ...sx }}>
      <Box sx={zen.sectionTitleSx}>{title}</Box>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Box>
  );
}

const TIPO_AUT_FISCAL = [
  { value: 0, label: 'Desobrigado' },
  { value: 2, label: 'NFC-e' },
];

const TIPOS_IMPRESSORA = [
  { value: 0, label: 'Texto' },
  { value: 1, label: 'Epson' },
  { value: 2, label: 'Bematech' },
  { value: 3, label: 'Daruma' },
  { value: 4, label: 'Vox' },
  { value: 5, label: 'Diebold' },
  { value: 6, label: 'Epson P2' },
  { value: 7, label: 'CustomPos' },
  { value: 8, label: 'Star' },
  { value: 9, label: 'Zjiang' },
  { value: 10, label: 'GPrinter' },
  { value: 11, label: 'Datecs' },
  { value: 12, label: 'Sunmi' },
  { value: 13, label: 'Externo' },
  { value: 14, label: 'Deskjet/PDF' },
];

const TIPOS_TERMINAL = [
  { value: 'caixa', label: 'Caixa (PDV)', hint: 'Venda no balcão / checkout' },
  { value: 'zenpay', label: 'ZenPay (POS Stone)', hint: 'Terminal remoto — recebe cobranças TEF do caixa' },
  { value: 'preVenda', label: 'Pré-venda', hint: 'Só monta pedido; outro terminal emite' },
  { value: 'autoAtendimento', label: 'Autoatendimento', hint: 'Totem / quiosque' },
  { value: 'inventario', label: 'Terminal de inventário', hint: 'Só contagem de estoque' },
];

function tipoTerminalFromData(data) {
  if (data?.zenPay) return 'zenpay';
  if (data?.gerencial) return 'inventario';
  if (data?.autoAtendimento) return 'autoAtendimento';
  if (data?.preVenda) return 'preVenda';
  return 'caixa';
}

function flagsFromTipoTerminal(tipo) {
  if (tipo === 'zenpay') {
    return { preVenda: false, autoAtendimento: false, gerencial: false };
  }
  if (tipo === 'caixa') {
    return { preVenda: false, autoAtendimento: false, gerencial: false };
  }
  return flagsFromModo(tipo);
}

const MODOS = [
  { value: 'normal', label: 'PDV (venda)', hint: 'Caixa e venda normais' },
  { value: 'preVenda', label: 'Pré-venda', hint: 'Só monta pedido; outro terminal emite' },
  { value: 'autoAtendimento', label: 'Autoatendimento', hint: 'Totem / quiosque' },
  { value: 'inventario', label: 'Terminal de inventário', hint: 'Só contagem de estoque' },
];

function modoFromFlags(data) {
  if (data?.gerencial) return 'inventario';
  if (data?.autoAtendimento) return 'autoAtendimento';
  if (data?.preVenda) return 'preVenda';
  return 'normal';
}

function flagsFromModo(modo) {
  return {
    preVenda: modo === 'preVenda',
    autoAtendimento: modo === 'autoAtendimento',
    gerencial: modo === 'inventario',
  };
}

/** Bool camel/Pascal — evita gravar false quando a API omite ou o form ainda não hidratou. */
function flagFrom(data, key, fallback = false) {
  if (!data || typeof data !== 'object') return fallback;
  const pascal = key.charAt(0).toUpperCase() + key.slice(1);
  const v = data[key] ?? data[pascal];
  if (v == null) return fallback;
  return v === true || v === 1 || v === '1' || v === 'true';
}

export default function PdvForm({
  embedded = false,
  pdvId = null,
  initialTipoTerminal = null,
  onSaved,
  onCancel,
}) {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const id = pdvId ?? params.id;
  const isEditMode = !!id;
  const navigate = useNavigate();
  const { isAtivo } = useModulosLicenca();
  const multiEstoque = isAtivo('ADD_MULTI_ESTOQUE');
  const tipoInicial = initialTipoTerminal
    || searchParams.get('tipo')
    || 'caixa';

  const [locaisEstoque, setLocaisEstoque] = useState([]);
  const [form, setForm] = useState({
    id: 0,
    descricao: '',
    estabelecimentoId: '',
    localEstoqueId: '',
    codigoInstalacao: '',
    ativo: true,
    tipoTerminal: tipoInicial,

    imprimeCancelamentos: true,
    imprimeCupomPresente: false,
    tipoAutFiscal: 0,
    impressora: '',
    marcaImpressora: 0,
    viasImpressao: 1,
    nroColunas: 48,
    perguntaSeImprime: false,
    mensagemRodapeCupom: '',
    impressoraDanfe: '',

    balancaPorta: '',
    balancaConfig: '',
    balancaPasta: '',
    balancaMarca: 'ambas',
    cargaBalanca: false,

    layoutVersao: '',
    layoutVenda: 1,
    pedeQuantidade: true,
    solicitaCpf: true,
    solicitaWhatsAppVenda: false,
    solicitaCadastroResumido: false,
    solicitaCupomPresenteWhatsApp: false,
    intervaloAtualizacaoSegundos: 120,
    tempoInatividadeAutoSegundos: 20,
    imprimeComprovanteMovimento: true,
    imprimeFechamentoCaixa: true,
    digitarConferenciaCaixa: true,
    solicitaVendedor: false,

    impressoraEtiquetas: '',
    geraEtiquetas: false,

    tefAtivo: false,
    tefTipo: 'SiTEF',
    tefRemotoPdvId: '',
    tefIpSitef: '',
    tefLoja: '',
    tefTerminal: '',
    tefLibraryName: '',
    tefHomologacao: true,
    tefValorMinimoParcela: 50,
    tefMaximoParcelas: 12,
    tefToken: '',

    plataforma: '',
    versaoApp: '',
  });

  const [estabs, setEstabs] = useState([]);
  const [todosPdvs, setTodosPdvs] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [solicitandoCarga, setSolicitandoCarga] = useState(false);
  /** Em edição, só permite salvar depois de carregar o PDV (evita zerar flags). */
  const [hydrated, setHydrated] = useState(!isEditMode);

  const titulo = useMemo(() => {
    if (isEditMode) return `Editar PDV #${id}`;
    const tipo = TIPOS_TERMINAL.find((t) => t.value === form.tipoTerminal);
    return tipo ? `Novo — ${tipo.label}` : 'Cadastro de PDV';
  }, [isEditMode, id, form.tipoTerminal]);

  const isZenPay = form.tipoTerminal === 'zenpay';
  const isCaixa = form.tipoTerminal === 'caixa';
  const isAuto = form.tipoTerminal === 'autoAtendimento';
  const mostraFiscal = isCaixa || form.tipoTerminal === 'preVenda' || isAuto;
  const mostraImpressao = isCaixa || form.tipoTerminal === 'preVenda' || isAuto;
  const mostraBalanca = isCaixa;
  const mostraComportamento = isCaixa;
  const mostraLayoutAuto = isAuto;
  const mostraTefCaixa = isCaixa;

  const terminaisZenPay = useMemo(() => {
    const estabId = Number(form.estabelecimentoId);
    if (!estabId) return [];
    return todosPdvs.filter((p) =>
      p.zenPay &&
      p.estabelecimentoId === estabId &&
      (!isEditMode || p.id !== Number(id))
    );
  }, [todosPdvs, form.estabelecimentoId, isEditMode, id]);

  const terminalZenPayVinculado = useMemo(() => {
    const remotoId = Number(form.tefRemotoPdvId);
    if (!remotoId) return null;
    return terminaisZenPay.find((p) => p.id === remotoId) || null;
  }, [terminaisZenPay, form.tefRemotoPdvId]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getEstabelecimentos();
        const lista = Array.isArray(data) ? data : (data ? [data] : []);
        setEstabs(
          lista.map(e => ({
            id: e.id,
            nomeFantasia: e.nomeFantasia || e.razaoSocial || `Estab. ${e.id}`,
          }))
        );
        if (lista.length === 1 && !form.estabelecimentoId) {
          setForm(f => ({ ...f, estabelecimentoId: lista[0].id }));
        }
      } catch (err) {
        setErrorMessage(getApiErrorMessage(err, 'Não foi possível carregar os estabelecimentos.'));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const estab = Number(form.estabelecimentoId);
    if (!estab) {
      setLocaisEstoque([]);
      return undefined;
    }
    let ativo = true;
    getLocaisEstoque(estab)
      .then(({ data }) => {
        if (!ativo) return;
        const lista = (Array.isArray(data) ? data : []).filter((l) => l.ativo !== false);
        const visiveis = multiEstoque ? lista : lista.filter((l) => l.principal);
        setLocaisEstoque(visiveis.length ? visiveis : lista);
        setForm((f) => {
          if (Number(f.estabelecimentoId) !== estab) return f;
          const atual = Number(f.localEstoqueId);
          if (lista.some((l) => Number(l.id) === atual)) return f;
          const principal = lista.find((l) => l.principal) || lista[0];
          return { ...f, localEstoqueId: principal ? principal.id : '' };
        });
      })
      .catch(() => {
        if (ativo) setLocaisEstoque([]);
      });
    return () => { ativo = false; };
  }, [form.estabelecimentoId, multiEstoque]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getAllPdvs();
        setTodosPdvs(Array.isArray(data) ? data : []);
      } catch {
        setTodosPdvs([]);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!isEditMode) {
        setHydrated(true);
        return;
      }
      setHydrated(false);
      try {
        const { data } = await getPdvById(id);
        const vias = Number(data?.viasImpressao);
        setForm((p) => ({
          ...p,
          ...data,
          estabelecimentoId: data?.estabelecimentoId ?? '',
          localEstoqueId: data?.localEstoqueId ?? '',
          tipoTerminal: tipoTerminalFromData(data),
          viasImpressao: vias > 0 ? vias : 1,
          balancaPasta: data?.balancaPasta || '',
          balancaMarca: data?.balancaMarca || 'ambas',
          plataforma: data?.plataforma || '',
          versaoApp: data?.versaoApp || '',
          tefTipo: data?.tefTipo || 'SiTEF',
          tefRemotoPdvId: data?.tefRemotoPdvId ?? '',
          // Flags críticas: leitura explícita (camel/Pascal) — não depender só do spread.
          ativo: flagFrom(data, 'ativo', true),
          imprimeCancelamentos: flagFrom(data, 'imprimeCancelamentos', true),
          imprimeCupomPresente: flagFrom(data, 'imprimeCupomPresente', false),
          perguntaSeImprime: flagFrom(data, 'perguntaSeImprime', false),
          pedeQuantidade: flagFrom(data, 'pedeQuantidade', true),
          solicitaCpf: flagFrom(data, 'solicitaCpf', true),
          solicitaWhatsAppVenda: flagFrom(data, 'solicitaWhatsAppVenda', false),
          solicitaCadastroResumido: flagFrom(data, 'solicitaCadastroResumido', false),
          solicitaCupomPresenteWhatsApp: flagFrom(data, 'solicitaCupomPresenteWhatsApp', false),
          imprimeComprovanteMovimento: flagFrom(data, 'imprimeComprovanteMovimento', true),
          imprimeFechamentoCaixa: flagFrom(data, 'imprimeFechamentoCaixa', true),
          digitarConferenciaCaixa: flagFrom(data, 'digitarConferenciaCaixa', true),
          solicitaVendedor: flagFrom(data, 'solicitaVendedor', false),
          cargaBalanca: flagFrom(data, 'cargaBalanca', false),
          geraEtiquetas: flagFrom(data, 'geraEtiquetas', false),
          tefAtivo: flagFrom(data, 'tefAtivo', false),
          tefHomologacao: flagFrom(data, 'tefHomologacao', true),
        }));
        setHydrated(true);
      } catch (err) {
        setErrorMessage(getApiErrorMessage(err, 'Não foi possível carregar o PDV.'));
        setHydrated(false);
      }
    })();
  }, [isEditMode, id]);

  const onChange = (field) => (e) => {
    const isCheckbox = e.target.type === 'checkbox';
    const val = isCheckbox ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: val }));
  };
  const onChangeNumber = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value === '' ? '' : Number(e.target.value) }));

  const validar = () => {
    if (!form.descricao.trim()) throw new Error('Descrição é obrigatória.');
    if (!String(form.estabelecimentoId).trim()) throw new Error('Estabelecimento é obrigatório.');
    if (!form.codigoInstalacao.trim()) throw new Error('Código de instalação é obrigatório.');
    if (form.nroColunas <= 0) throw new Error('Nº de colunas deve ser maior que zero.');
    const vias = Number(form.viasImpressao);
    if (!Number.isFinite(vias) || vias < 1) throw new Error('Vias de impressão deve ser no mínimo 1.');
    if (![0, 2].includes(Number(form.tipoAutFiscal))) {
      throw new Error('Tipo de autorização fiscal inválido.');
    }
    if (form.cargaBalanca && !String(form.balancaPasta || '').trim()) {
      throw new Error('Informe a pasta onde o PDV gravará a carga da balança (ex.: C:\\Users\\Public\\Balanca).');
    }
    if (form.tipoTerminal === 'zenpay') return;

    if (form.tipoTerminal === 'caixa' && form.tefAtivo) {
      const tipo = (form.tefTipo || 'SiTEF').toUpperCase();
      if (tipo === 'ZENPAY') {
        if (!form.tefRemotoPdvId) {
          throw new Error('Selecione o terminal ZenPay vinculado a este caixa.');
        }
      } else {
        if (!String(form.tefIpSitef || '').trim()) throw new Error('Informe o IP SiTef.');
        if (!String(form.tefLoja || '').trim()) throw new Error('Informe a loja SiTef.');
        if (!String(form.tefTerminal || '').trim()) throw new Error('Informe o terminal SiTef.');
      }
    }
  };

  const fechar = () => {
    if (onCancel) onCancel();
    else navigate('/pdvs');
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setInfoMessage('');
    if (isEditMode && !hydrated) {
      setErrorMessage('Aguarde o carregamento do PDV antes de salvar.');
      return;
    }
    setSaving(true);
    try {
      validar();
      const modoFlags = flagsFromTipoTerminal(form.tipoTerminal);
      const zenPay = form.tipoTerminal === 'zenpay';
      const vias = Math.max(1, Number(form.viasImpressao) || 1);

      const payload = {
        id: isEditMode ? Number(id) : 0,
        descricao: form.descricao,
        estabelecimentoId: Number(form.estabelecimentoId),
        localEstoqueId: form.localEstoqueId ? Number(form.localEstoqueId) : null,
        codigoInstalacao: form.codigoInstalacao.trim().toUpperCase(),
        ativo: !!form.ativo,

        ...modoFlags,
        imprimeCancelamentos: !!form.imprimeCancelamentos,
        imprimeCupomPresente: !!form.imprimeCupomPresente,

        tipoAutFiscal: Number(form.tipoAutFiscal),
        impressora: form.impressora,
        marcaImpressora: Number(form.marcaImpressora),
        viasImpressao: vias,
        nroColunas: Number(form.nroColunas),
        perguntaSeImprime: !!form.perguntaSeImprime,
        mensagemRodapeCupom: (form.mensagemRodapeCupom || '').trim() || null,
        impressoraDanfe: form.impressoraDanfe || '',
        pedeQuantidade: !!form.pedeQuantidade,
        solicitaCpf: !!form.solicitaCpf,
        solicitaWhatsAppVenda: !!form.solicitaWhatsAppVenda,
        solicitaCadastroResumido: !!form.solicitaCadastroResumido,
        solicitaCupomPresenteWhatsApp: !!form.solicitaCupomPresenteWhatsApp,
        intervaloAtualizacaoSegundos: Math.min(
          3600,
          Math.max(30, Number(form.intervaloAtualizacaoSegundos) || 120),
        ),
        tempoInatividadeAutoSegundos: Math.min(
          600,
          Math.max(5, Number(form.tempoInatividadeAutoSegundos) || 20),
        ),

        balancaPorta: form.balancaPorta || '',
        balancaConfig: form.balancaConfig || '',
        balancaPasta: (form.balancaPasta || '').trim(),
        balancaMarca: form.balancaMarca || 'ambas',
        cargaBalanca: !!form.cargaBalanca,

        // campos legados / não usados na UI — mantém valores seguros
        qrCodeLateral: false,
        imprimeObsPagto: false,
        nroSerieSat: '',
        codigoAtivacao: '',
        assinaturaSat: '',
        layoutVersao: form.layoutVersao || '',
        layoutVenda: Number(form.layoutVenda) || 0,
        imprimeComprovanteMovimento: !!form.imprimeComprovanteMovimento,
        imprimeFechamentoCaixa: !!form.imprimeFechamentoCaixa,
        digitarConferenciaCaixa: !!form.digitarConferenciaCaixa,
        solicitaVendedor: !!form.solicitaVendedor,
        tema: 'Azul',
        logoSistema: null,
        logoImpressos: null,
        impressoraEtiquetas: form.impressoraEtiquetas || '',
        geraEtiquetas: !!form.geraEtiquetas,

        tefAtivo: !!form.tefAtivo,
        tefTipo: zenPay ? 'SiTEF' : (form.tefTipo || 'SiTEF'),
        tefRemotoPdvId: zenPay || !form.tefAtivo || form.tefTipo !== 'ZenPay'
          ? null
          : (Number(form.tefRemotoPdvId) || null),
        zenPay,
        tefIpSitef: form.tefIpSitef || null,
        tefLoja: form.tefLoja || null,
        tefTerminal: form.tefTerminal || null,
        tefLibraryName: form.tefLibraryName || null,
        tefHomologacao: !!form.tefHomologacao,
        tefValorMinimoParcela: Number(form.tefValorMinimoParcela) || 0,
        tefMaximoParcelas: Number(form.tefMaximoParcelas) || 1,
        tefToken: form.tefToken || null,
      };

      if (isEditMode) await updatePdv(Number(id), payload);
      else await createPdv(payload);

      if (onSaved) onSaved(isEditMode ? 'PDV atualizado' : 'PDV criado');
      else navigate('/pdvs');
    } catch (err) {
      setErrorMessage(getApiErrorMessage(err, 'Erro ao salvar PDV.'));
    } finally {
      setSaving(false);
    }
  };

  const solicitarCarga = async () => {
    if (!isEditMode) return;
    setErrorMessage('');
    setInfoMessage('');
    setSolicitandoCarga(true);
    try {
      if (!form.cargaBalanca || !String(form.balancaPasta || '').trim()) {
        throw new Error('Salve o PDV com “Carga para balança” e a pasta configurada antes de solicitar.');
      }
      await solicitarCargaBalancaPdv(Number(id));
      setInfoMessage(
        `Carga solicitada. Na próxima sincronização, o PDV gravará os arquivos em ${form.balancaPasta}.`
      );
    } catch (err) {
      setErrorMessage(getApiErrorMessage(err, 'Não foi possível solicitar a carga.'));
    } finally {
      setSolicitandoCarga(false);
    }
  };

  return (
    <Box component="form" onSubmit={onSubmit} sx={{ p: embedded ? 0 : 3, maxWidth: embedded ? 'none' : 1100, mx: 'auto' }}>
      {!embedded && (
        <Typography variant="h5" sx={{ textAlign: 'center', color: zen.blue, fontWeight: 900, mb: 2 }}>
          {titulo}
        </Typography>
      )}

      <Section title="Identificação" sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <TextField label="Descrição" value={form.descricao} onChange={onChange('descricao')} fullWidth required />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Estabelecimento"
              select
              value={form.estabelecimentoId}
              onChange={onChange('estabelecimentoId')}
              fullWidth
              required
            >
              {estabs.map(e => (
                <MenuItem key={e.id} value={e.id}>{e.nomeFantasia}</MenuItem>
              ))}
            </TextField>
          </Grid>
          {multiEstoque && (
          <Grid item xs={12} md={3}>
            <TextField
              label="Local de estoque"
              select
              value={form.localEstoqueId || ''}
              onChange={onChange('localEstoqueId')}
              fullWidth
              helperText="De onde este PDV vende. Loja e sobreloja podem ser locais diferentes."
            >
              {locaisEstoque.map((l) => (
                <MenuItem key={l.id} value={l.id}>
                  {l.nome}{l.principal ? ' (principal)' : ''}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          )}
          <Grid item xs={12} md={3}>
            <TextField
              label="Código de instalação"
              value={form.codigoInstalacao}
              onChange={(e) => setForm((prev) => ({
                ...prev,
                codigoInstalacao: e.target.value.toUpperCase(),
              }))}
              helperText="Da tela de ativação do app"
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControlLabel control={<Checkbox checked={form.ativo} onChange={onChange('ativo')} />} label="Ativo" />
          </Grid>
          {isEditMode && (form.plataforma || form.versaoApp) && (
            <Grid item xs={12} md={9}>
              <Chip
                color="primary"
                variant="outlined"
                label={`Terminal: ${form.plataforma || '—'}${form.versaoApp ? ` · ${form.versaoApp}` : ''}`}
              />
            </Grid>
          )}
        </Grid>
      </Section>

      <Section title="Tipo de terminal" sx={{ mb: 2 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Escolha o perfil deste equipamento. Cada tipo exibe apenas os campos relevantes.
          {isZenPay && ' O ZenPay é um terminal Stone que só processa cobranças remotas do caixa.'}
        </Alert>
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend" sx={{ mb: 1 }}>Perfil</FormLabel>
          <RadioGroup
            value={form.tipoTerminal}
            onChange={(e) => {
              const tipo = e.target.value;
              setForm((prev) => ({
                ...prev,
                tipoTerminal: tipo,
                tefAtivo: tipo === 'zenpay' ? false : prev.tefAtivo,
                tefRemotoPdvId: tipo === 'zenpay' ? '' : prev.tefRemotoPdvId,
              }));
            }}
          >
            {TIPOS_TERMINAL.map((m) => (
              <FormControlLabel
                key={m.value}
                value={m.value}
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1">{m.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{m.hint}</Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', mb: 0.5 }}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Section>

      {isZenPay && (
        <Section title="ZenPay" sx={{ mb: 2 }}>
          <Alert severity="success">
            Cadastre este PDV no app <strong>ZenPay</strong> com o mesmo código de instalação.
            Um terminal ZenPay pode ficar vinculado a <strong>um único</strong> caixa por vez.
            A galeria promocional e os usuários são sincronizados do Gestão.
          </Alert>
        </Section>
      )}

      {mostraFiscal && (
      <Section title="Fiscal" sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Autorização fiscal"
              select
              value={Number(form.tipoAutFiscal) === 1 ? 0 : form.tipoAutFiscal}
              onChange={onChangeNumber('tipoAutFiscal')}
              fullWidth
            >
              {TIPO_AUT_FISCAL.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={8}>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <FormControlLabel control={<Checkbox checked={!!form.solicitaCpf} onChange={onChange('solicitaCpf')} />} label="Solicitar CPF/CNPJ" />
              <FormControlLabel control={<Checkbox checked={!!form.solicitaWhatsAppVenda} onChange={onChange('solicitaWhatsAppVenda')} />} label="Cupom no WhatsApp" />
              <FormControlLabel control={<Checkbox checked={!!form.solicitaCadastroResumido} onChange={onChange('solicitaCadastroResumido')} />} label="Cadastro resumido no WhatsApp" />
              <FormControlLabel control={<Checkbox checked={!!form.solicitaCupomPresenteWhatsApp} onChange={onChange('solicitaCupomPresenteWhatsApp')} />} label="Perguntar Cupom Presente no WhatsApp" />
            </Stack>
          </Grid>
        </Grid>
      </Section>
      )}

      {mostraImpressao && (
      <Section title="Impressão" sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <TextField
              label="Impressora de cupom (nome/spool ou rede)"
              value={form.impressora}
              onChange={onChange('impressora')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Tipo / marca"
              select
              value={form.marcaImpressora}
              onChange={onChangeNumber('marcaImpressora')}
              fullWidth
            >
              {TIPOS_IMPRESSORA.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Vias"
              type="number"
              value={form.viasImpressao}
              onChange={onChangeNumber('viasImpressao')}
              fullWidth
              inputProps={{ min: 1 }}
              helperText="Padrão 1"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Colunas"
              type="number"
              value={form.nroColunas}
              onChange={onChangeNumber('nroColunas')}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Ex.: 32, 42, 48 ESC/POS">
                      <IconButton size="small"><InfoOutlinedIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Impressora DANFE (A4)"
              value={form.impressoraDanfe}
              onChange={onChange('impressoraDanfe')}
              fullWidth
              helperText="Usada quando o PDV gerar DANFE em impressora A4"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ pt: 1 }}>
              <FormControlLabel control={<Checkbox checked={form.perguntaSeImprime} onChange={onChange('perguntaSeImprime')} />} label="Perguntar se imprime" />
              <FormControlLabel control={<Checkbox checked={form.imprimeCancelamentos} onChange={onChange('imprimeCancelamentos')} />} label="Imprimir cancelamentos" />
              <FormControlLabel control={<Checkbox checked={!!form.imprimeCupomPresente} onChange={onChange('imprimeCupomPresente')} />} label="Imprimir Cupom Presente" />
              <FormControlLabel control={<Checkbox checked={form.imprimeComprovanteMovimento} onChange={onChange('imprimeComprovanteMovimento')} />} label="Comprovante de movimento" />
              <FormControlLabel control={<Checkbox checked={form.imprimeFechamentoCaixa} onChange={onChange('imprimeFechamentoCaixa')} />} label="Fechamento de caixa" />
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Mensagem rodapé do cupom"
              value={form.mensagemRodapeCupom || ''}
              onChange={onChange('mensagemRodapeCupom')}
              fullWidth
              multiline
              minRows={2}
              maxRows={6}
              inputProps={{ maxLength: 2000 }}
              helperText="Macros: {data} {hora} {dataHora} {operador} {cliente} {clienteDocumento} {venda} {pdv} {estabelecimento} {cnpj} {total} {troco}. Enter = nova linha. Vazio = padrão (Obrigado / ZenPDV)."
            />
          </Grid>
        </Grid>
      </Section>
      )}

      {mostraBalanca && (
      <Section title="Balança e etiquetas" sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={form.cargaBalanca} onChange={onChange('cargaBalanca')} />}
              label="Carga para balança (MGV escuta pasta no PC deste PDV)"
            />
          </Grid>
          {form.cargaBalanca && (
            <>
              <Grid item xs={12} md={8}>
                <TextField
                  label="Pasta da carga no PC do PDV"
                  value={form.balancaPasta}
                  onChange={onChange('balancaPasta')}
                  fullWidth
                  required
                  placeholder="C:\Users\Public\Balanca"
                  helperText="O PDV grava TXITENS.txt / CADTXT.txt aqui ao receber a solicitação do Gestão"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Formato"
                  select
                  value={form.balancaMarca || 'ambas'}
                  onChange={onChange('balancaMarca')}
                  fullWidth
                >
                  <MenuItem value="ambas">Toledo + Filizola</MenuItem>
                  <MenuItem value="toledo">Só Toledo (TXITENS.txt)</MenuItem>
                  <MenuItem value="filizola">Só Filizola (CADTXT.txt)</MenuItem>
                </TextField>
              </Grid>
              {isEditMode && (
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    startIcon={<ScaleIcon />}
                    disabled={solicitandoCarga}
                    onClick={solicitarCarga}
                  >
                    Solicitar carga agora neste PDV
                  </Button>
                </Grid>
              )}
            </>
          )}
          <Grid item xs={12} md={4}>
            <TextField label="Balança (porta checkout)" value={form.balancaPorta} onChange={onChange('balancaPorta')} fullWidth />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField label="Balança (config serial)" value={form.balancaConfig} onChange={onChange('balancaConfig')} fullWidth />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Impressora de etiquetas" value={form.impressoraEtiquetas} onChange={onChange('impressoraEtiquetas')} fullWidth />
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel control={<Checkbox checked={form.geraEtiquetas} onChange={onChange('geraEtiquetas')} />} label="Gera etiquetas neste terminal" />
          </Grid>
        </Grid>
      </Section>
      )}

      {mostraComportamento && (
      <Section title="Comportamento da venda" sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Layout da venda"
              select
              value={form.layoutVenda}
              onChange={onChangeNumber('layoutVenda')}
              fullWidth
            >
              <MenuItem value={0}>Simples</MenuItem>
              <MenuItem value={1}>Grade (produtos + categorias)</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Atualização automática (segundos)"
              type="number"
              value={form.intervaloAtualizacaoSegundos}
              onChange={onChangeNumber('intervaloAtualizacaoSegundos')}
              fullWidth
              inputProps={{ min: 30, max: 3600 }}
              helperText="Checagem silenciosa de cadastros no PDV (30–3600)"
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ pt: 1 }}>
              <FormControlLabel control={<Checkbox checked={form.pedeQuantidade} onChange={onChange('pedeQuantidade')} />} label="Pede quantidade" />
              <FormControlLabel control={<Checkbox checked={form.solicitaVendedor} onChange={onChange('solicitaVendedor')} />} label="Solicita vendedor" />
              <FormControlLabel control={<Checkbox checked={form.digitarConferenciaCaixa} onChange={onChange('digitarConferenciaCaixa')} />} label="Digitar conferência de caixa" />
            </Stack>
          </Grid>
        </Grid>
      </Section>
      )}

      {mostraLayoutAuto && (
      <Section title="Layout do autoatendimento" sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Layout da venda"
              select
              value={form.layoutVenda}
              onChange={onChangeNumber('layoutVenda')}
              fullWidth
              helperText="Simples = só leitor (sem vitrine) · Grade = produtos + categorias"
            >
              <MenuItem value={0}>Simples (só leitor)</MenuItem>
              <MenuItem value={1}>Grade (produtos + categorias)</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Inatividade (segundos)"
              type="number"
              value={form.tempoInatividadeAutoSegundos}
              onChange={onChangeNumber('tempoInatividadeAutoSegundos')}
              fullWidth
              inputProps={{ min: 5, max: 600 }}
              helperText="Limpa o pedido se ninguém mexer (carrinho e pagamento). Padrão 20."
            />
          </Grid>
        </Grid>
      </Section>
      )}

      {mostraTefCaixa && (
      <Section title="TEF no caixa" sx={{ mb: 2 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Formas com flag TEF usam SiTef (pinpad local) ou ZenPay (POS remoto).
          PIX com flag TEF segue a mesma integração ativa.
        </Alert>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={form.tefAtivo} onChange={onChange('tefAtivo')} />}
              label="TEF ativo neste caixa"
            />
          </Grid>

          {form.tefAtivo && (
            <>
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Tipo de integração TEF</FormLabel>
                  <RadioGroup
                    row
                    value={form.tefTipo || 'SiTEF'}
                    onChange={(e) => {
                      const tipo = e.target.value;
                      setForm((prev) => ({
                        ...prev,
                        tefTipo: tipo,
                        tefRemotoPdvId: tipo === 'ZenPay' ? prev.tefRemotoPdvId : '',
                      }));
                    }}
                  >
                    <FormControlLabel value="SiTEF" control={<Radio />} label="SiTef (pinpad local)" />
                    <FormControlLabel value="ZenPay" control={<Radio />} label="ZenPay (POS remoto)" />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {form.tefTipo === 'ZenPay' ? (
                <Grid item xs={12} md={8}>
                  <TextField
                    label="Terminal ZenPay vinculado"
                    select
                    value={form.tefRemotoPdvId || ''}
                    onChange={onChange('tefRemotoPdvId')}
                    fullWidth
                    required
                    helperText={
                      terminaisZenPay.length
                        ? 'Vínculo 1:1 — o POS processa pagamentos deste caixa'
                        : 'Cadastre um terminal ZenPay no mesmo estabelecimento'
                    }
                  >
                    <MenuItem value="">
                      <em>Selecione…</em>
                    </MenuItem>
                    {terminaisZenPay.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.descricao} ({p.codigoInstalacao})
                      </MenuItem>
                    ))}
                  </TextField>
                  {terminalZenPayVinculado?.plataforma && (
                    <Chip
                      size="small"
                      sx={{ mt: 1 }}
                      label={`Último contato: ${terminalZenPayVinculado.plataforma}${terminalZenPayVinculado.versaoApp ? ` · ${terminalZenPayVinculado.versaoApp}` : ''}`}
                    />
                  )}
                </Grid>
              ) : (
                <>
                  <Grid item xs={12}>
                    <Stack direction="row" spacing={2} flexWrap="wrap">
                      <FormControlLabel
                        control={<Checkbox checked={form.tefHomologacao} onChange={onChange('tefHomologacao')} />}
                        label="Homologação"
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField label="IP SiTef" value={form.tefIpSitef} onChange={onChange('tefIpSitef')} fullWidth required />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField label="Loja" value={form.tefLoja} onChange={onChange('tefLoja')} fullWidth required />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField label="Terminal" value={form.tefTerminal} onChange={onChange('tefTerminal')} fullWidth required />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Library"
                      value={form.tefLibraryName}
                      onChange={onChange('tefLibraryName')}
                      fullWidth
                      placeholder="CliSiTef64I.dll"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Valor mín. parcela"
                      type="number"
                      value={form.tefValorMinimoParcela}
                      onChange={onChangeNumber('tefValorMinimoParcela')}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Máx. parcelas"
                      type="number"
                      value={form.tefMaximoParcelas}
                      onChange={onChangeNumber('tefMaximoParcelas')}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField label="Token TEF" value={form.tefToken} onChange={onChange('tefToken')} fullWidth />
                  </Grid>
                </>
              )}
            </>
          )}
        </Grid>
      </Section>
      )}

      <Divider sx={{ my: 2 }} />

      <Stack direction="row" spacing={1} justifyContent="center">
        <Button
          type="submit"
          variant="contained"
          disabled={saving || (isEditMode && !hydrated)}
          startIcon={<SaveIcon />}
          sx={{ minWidth: 160, background: zen.blue, '&:hover': { background: zen.blueDark }, textTransform: 'none', fontWeight: 700 }}
        >
          {saving
            ? 'Salvando…'
            : isEditMode && !hydrated
              ? 'Carregando…'
              : isEditMode
                ? 'Salvar'
                : 'Cadastrar'}
        </Button>
        <Button
          variant="contained"
          color="inherit"
          onClick={fechar}
          startIcon={<CancelIcon />}
          sx={{ minWidth: 160, textTransform: 'none', fontWeight: 700 }}
        >
          Cancelar
        </Button>
      </Stack>

      <CenteredAlert
        open={!!errorMessage}
        variant="error"
        message={errorMessage}
        onClose={() => setErrorMessage('')}
      />
      <CenteredAlert
        open={!!infoMessage}
        variant="success"
        message={infoMessage}
        onClose={() => setInfoMessage('')}
      />
    </Box>
  );
}
